import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import '@/modernize.css';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import Organizations from '@/pages/Organizations';
import Outlets from '@/pages/Outlets';
import Users from '@/pages/Users';
import IntelligenceHub from '@/pages/IntelligenceHub';
import GlobalMasters from '@/pages/GlobalMasters';
import VendorAnalytics from '@/pages/VendorAnalytics';
import DeletionApprovals from '@/pages/DeletionApprovals';
import IngredientMaster from '@/pages/IngredientMaster';
import IngredientProposals from '@/pages/IngredientProposals';
import VendorMappings from '@/pages/VendorMappings';
import VendorCategoryMaster from '@/pages/VendorCategoryMaster';
import ItemCategoryMaster from '@/pages/ItemCategoryMaster';
import Themes from '@/pages/Themes';
import Configuration from '@/pages/Configuration';
import AuditLogs from '@/pages/AuditLogs';
import TrialAnalytics from '@/pages/TrialAnalytics';
import PriceAlerts from '@/pages/PriceAlerts';
import GovernanceFeed from '@/pages/GovernanceFeed';
import OrgApplications from '@/pages/OrgApplications';

// Protected Route wrapper component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="organizations" element={<Organizations />} />
                <Route path="outlets" element={<Outlets />} />
                <Route path="users" element={<Users />} />
                <Route path="intelligence" element={<IntelligenceHub />} />
                <Route path="price-alerts" element={<Navigate to="/intelligence" replace />} />
                {/* Expense Intelligence retired — owners: Bills (expenses), Accounts (cash). */}
                <Route path="expense-intelligence" element={<Navigate to="/intelligence" replace />} />
                <Route path="masters" element={<GlobalMasters />} />
                <Route path="vendor-analytics" element={<VendorAnalytics />} />
                <Route path="deletion-approvals" element={<DeletionApprovals />} />
                <Route path="ingredient-master" element={<IngredientMaster />} />
                <Route path="ingredient-proposals" element={<IngredientProposals />} />
                <Route path="vendor-mappings" element={<VendorMappings />} />
                <Route path="vendor-categories" element={<VendorCategoryMaster />} />
                <Route path="item-categories" element={<ItemCategoryMaster />} />
                <Route path="themes" element={<Themes />} />
                <Route path="configuration" element={<Configuration />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="governance" element={<GovernanceFeed />} />
                <Route path="org-modules" element={<OrgApplications />} />
                <Route path="applications" element={<OrgApplications />} />
                <Route path="trial-analytics" element={<TrialAnalytics />} />
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
